import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import api from '../services/api';
import TokenUsageHistory from './TokenUsageHistory';

const { FiX, FiUser, FiKey, FiSave, FiLoader, FiChevronDown, FiChevronUp } = FiIcons;

const ProfileModal = ({ isOpen, onClose }) => {
  const [profile, setProfile] = useState(null);
  const [promptForTranscribe, setPromptForTranscribe] = useState('');
  const [promptForSummary, setPromptForSummary] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [isApiKeyValid, setIsApiKeyValid] = useState(false);
  const [isCheckingApiKey, setIsCheckingApiKey] = useState(false);
  const [apiKeyStatus, setApiKeyStatus] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingKey, setIsSavingKey] = useState(false);
  const [error, setError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');

  const handleLogout = () => {
    // This should ideally call a logout function from a context or service
    localStorage.removeItem('token');
    window.location.href = '/login';
  };

  useEffect(() => {
    if (isOpen) {
      const fetchProfile = async () => {
        setIsLoading(true);
        setError(null);
        try {
          const response = await api.getProfileDetails();
          if (response) {
            setProfile(response);
            setPromptForTranscribe(response.prompt_for_transcribe || '');
            setPromptForSummary(response.prompt_for_summary || '');
          } else {
            setError('Could not fetch profile data.');
          }
        } catch (err) {
          setError(err.message || 'An error occurred.');
        } finally {
          setIsLoading(false);
        }
      };
      fetchProfile();
    }
  }, [isOpen]);

  const handleSavePrompts = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await api.updateUserPrompts({
        prompt_for_transcribe: promptForTranscribe,
        prompt_for_summary: promptForSummary,
      });
      setConfirmationMessage('บันทึกสำเร็จ! กรุณาเข้าสู่ระบบใหม่เพื่ออัปเดตการตั้งค่า');
      setShowConfirmation(true);
    } catch (err) {
      setError(err.message || 'Failed to save prompts.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveApiKey = async () => {
    if (!isApiKeyValid) {
      setApiKeyStatus('Please validate the key before saving.');
      return;
    }
    setIsSavingKey(true);
    setError(null);
    try {
      await api.updateApiKey({ gemini_api_key: apiKey });
      setApiKey('');
      setIsApiKeyValid(false);
      setApiKeyStatus('API Key saved successfully!');
      setConfirmationMessage('API Key บันทึกสำเร็จ! กรุณาเข้าสู่ระบบใหม่เพื่ออัปเดตการตั้งค่า');
      setShowConfirmation(true);
    } catch (err) {
      setError(err.message || 'Failed to save API key.');
    } finally {
      setIsSavingKey(false);
    }
  };

  const handleCheckApiKey = async () => {
    setIsCheckingApiKey(true);
    setApiKeyStatus('');
    const result = await api.checkApiKey(apiKey);
    if (result.success) {
      setIsApiKeyValid(true);
      setApiKeyStatus('API Key is valid!');
    } else {
      setIsApiKeyValid(false);
      setApiKeyStatus('Invalid API Key.');
    }
    setIsCheckingApiKey(false);
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
  };

  return (
    <AnimatePresence>
      {isOpen && !showConfirmation && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white rounded-lg shadow-xl w-full max-w-md"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-lg font-semibold">User Profile</h2>
              <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-200">
                <SafeIcon icon={FiX} />
              </button>
            </div>

            <div className="p-4 sm:p-6 max-h-[80vh] overflow-y-auto">
              {isLoading && (
                <div className="flex justify-center items-center h-40">
                  <SafeIcon icon={FiLoader} className="animate-spin h-8 w-8 text-gray-500" />
                </div>
              )}
              {error && <div className="text-red-500 bg-red-100 p-3 rounded-md">{error}</div>}

              {!isLoading && !error && profile && (
                <div className="space-y-6 prose prose-sm prose-green">
                  <div className="flex flex-col sm:flex-row items-center gap-4">
                    <img
                      src={profile.profile_image_url || '/images/yy_default.jpg'}
                      alt="Profile"
                      className="w-24 h-24 rounded-full object-cover border-4 border-green-200"
                    />
                    <div className="text-center sm:text-left">
                      <p className="font-semibold text-xl">{profile.nickname || 'N/A'}</p>
                      <p className="text-sm text-gray-600">{profile.email}</p>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <h3 className="font-semibold text-md">Token Usage</h3>
                      <button
                        onClick={() => setShowHistory(!showHistory)}
                        className="text-sm text-green-600 hover:underline flex items-center gap-1"
                      >
                        {showHistory ? 'Hide Details' : 'Show Details'}
                        <SafeIcon icon={showHistory ? FiChevronUp : FiChevronDown} />
                      </button>
                    </div>
                    <p className="text-3xl font-bold mt-1">{profile.total_token_used.toLocaleString()}</p>
                    <p className="text-sm">
                      Aprox. (USD) {(profile.total_token_used / 1000000 * 30).toFixed(2)}
                    </p>
                    <AnimatePresence>
                      {showHistory && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                        >
                          <TokenUsageHistory usageRecords={profile.token_usages || []} />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold text-md mb-2">คำสั่งสำหรับถอดความ</h3>
                      <textarea
                        value={promptForTranscribe}
                        onChange={(e) => setPromptForTranscribe(e.target.value)}
                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500 transition"
                        rows="3"
                        placeholder="e.g., Transcribe the audio clearly, focusing on technical terms."
                      />
                    </div>
                    <div>
                      <h3 className="font-semibold text-md mb-2">คำสั่งสำหรับสรุป</h3>
                      <textarea
                        value={promptForSummary}
                        onChange={(e) => setPromptForSummary(e.target.value)}
                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500 transition"
                        rows="3"
                        placeholder="e.g., Summarize the key decisions and action items from the meeting."
                      />
                    </div>
                    <button
                      onClick={handleSavePrompts}
                      disabled={isSaving}
                      className="w-full flex justify-center items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 transition"
                    >
                      <SafeIcon icon={isSaving ? FiLoader : FiSave} className={isSaving ? 'animate-spin' : ''} />
                      {isSaving ? 'Saving...' : 'Save Prompts'}
                    </button>
                  </div>

                  <div className="space-y-2">
                    <h3 className="font-semibold text-md">Gemini API Key</h3>
                    <input
                      type="password"
                      value={apiKey}
                      onChange={(e) => {
                        setApiKey(e.target.value);
                        setIsApiKeyValid(false);
                        setApiKeyStatus('');
                      }}
                      className="w-full p-2 border rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500 transition"
                      placeholder="Enter your new API key..."
                    />
                    <div className="flex flex-col sm:flex-row gap-2 mt-2">
                      <button
                        onClick={handleCheckApiKey}
                        disabled={isCheckingApiKey || !apiKey}
                        className="w-full sm:w-1/2 flex justify-center items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400 transition"
                      >
                        <SafeIcon icon={isCheckingApiKey ? FiLoader : FiKey} className={isCheckingApiKey ? 'animate-spin' : ''} />
                        {isCheckingApiKey ? 'Checking...' : 'Check Key'}
                      </button>
                      <button
                        onClick={handleSaveApiKey}
                        disabled={isSavingKey || !isApiKeyValid}
                        className="w-full sm:w-1/2 flex justify-center items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 transition"
                      >
                        <SafeIcon icon={isSavingKey ? FiLoader : FiSave} className={isSavingKey ? 'animate-spin' : ''} />
                        {isSavingKey ? 'Saving...' : 'Save API Key'}
                      </button>
                    </div>
                    {apiKeyStatus && (
                      <p className={`mt-2 text-sm text-center sm:text-left ${isApiKeyValid ? 'text-green-600' : 'text-red-600'}`}>
                        {apiKeyStatus}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}

      {showConfirmation && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white rounded-lg shadow-xl w-full max-w-sm"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div className="p-6 text-center">
              <h3 className="text-lg font-semibold mb-4">{confirmationMessage}</h3>
              <div className="flex justify-center gap-4">
                <button
                  onClick={() => {
                    setShowConfirmation(false);
                    onClose();
                  }}
                  className="px-6 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowConfirmation(false);
                    handleLogout();
                  }}
                  className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition"
                >
                  OK
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProfileModal;
